import { Component, OnDestroy, OnInit } from '@angular/core';
import { GlobalEngagementService } from '../../http/global-engagement/global-engagement.service';
import { Subject, takeUntil } from 'rxjs';
import { GlobalEngagementLeftNavModel } from '../../common/models/global-engagement.model';
import { ActivatedRoute, Router } from '@angular/router';
import { PermissionsObj } from '../../common/models/common-models';
import { PermissionCheckService } from '../../common/validations/permission-check.service';
import { SecurityWebapiService } from '../../http/security/security-webapi.service';
import {
  EntityDetails,
  EntityType,
  UserService,
} from '../../http/user.service';
import { HttpErrorResponse } from '@angular/common/http';
import { EclipseHeaderService } from '../../http/eclipse-header.service';
import { GlobalEngagementsConstant } from '../constants/global-engagements.constants';
import { GlobalEngagementSubmissionDocumentStatus } from '../constants/global_engagement_submission_status.enum';
import { SubmissionFormValidEmitter } from '../../common/models/external-communication.model';
import { StatusEnum } from '../../common/constants/status-enum';
import { CommonService } from '../../http/intake/common.service';

@Component({
  selector: 'app-global-engagement',
  templateUrl: './global-engagement.component.html',
  styleUrl: './global-engagement.component.less',
})
export class GlobalEngagementComponent implements OnInit, OnDestroy {
  activeIndex: number = -1;
  readonly #unsubscriber$: Subject<void> = new Subject<void>();
  leftNavDetails: GlobalEngagementLeftNavModel[] = [];
  _blank: string = GlobalEngagementsConstant.EMPTY_STRING;
  engagementTitle: string = this._blank;
  engagementId: number | null = null;
  activeSubmissionId: number | null = null;
  activeSubmissionType: string = this._blank;
  activeSubmissionStatus: string = this._blank;
  permissionObj: PermissionsObj = {} as PermissionsObj;
  queryParamSubmissionId: string = this._blank;
  isEngagementDetailsValid: boolean = false;
  subMissionStatusDraft: string =
    GlobalEngagementSubmissionDocumentStatus.DRAFT as string;

  constructor(
    private readonly globalEngagementService: GlobalEngagementService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly permissionCheck: PermissionCheckService,
    private readonly securityWebapiService: SecurityWebapiService,
    private readonly userService: UserService,
    private readonly headerService: EclipseHeaderService,
    private readonly commonService: CommonService
  ) {}

  ngOnInit(): void {
    this.fetchPermissions();
    const id: number | null = this.route.snapshot.paramMap.get('id')
      ? Number(this.route.snapshot.paramMap.get('id'))
      : null;
    if (id !== null) this.engagementId = id;
    {
      this.getLeftNavDetails(id);
    }
  }

  fetchPermissions(): void {
    this.permissionCheck.permissionsObj$
      .pipe(takeUntil(this.#unsubscriber$))
      .subscribe((permissions: PermissionsObj) => {
        this.permissionObj = { ...permissions };
      });
  }

  ngAfterViewInit(): void {
    this.scrollToTop();
  }

  backToHome(): void {
    this.router.navigate(['/']);
  }

  setActive(index: number): void {
    this.activeIndex = index;
    if (this.activeIndex !== -1) {
      this.activeSubmissionId = this.leftNavDetails[index].submissionId;
      this.activeSubmissionType = this.leftNavDetails[index].submissionType;
      this.activeSubmissionStatus =
        this.leftNavDetails[index].submissionsStatus;
    } else {
      this.activeSubmissionId = null;
      this.activeSubmissionType = this._blank;
      this.activeSubmissionStatus = this._blank;
    }

    /**
     * Reload should take to last active submission
     * It adds the queryParam for every submission selected
     */
    this.router.navigate(['globalEngagements/details', this.engagementId], {
      queryParams: { submissionId: this.activeSubmissionId },
      queryParamsHandling: 'merge',
      state: {
        submissionID: this.activeSubmissionId,
        submissionTypeCode: this.activeSubmissionType,
        name: this.engagementTitle,
      },
      replaceUrl: true,
    });

    if (index === -1) {
      this.updateRoleAndPermissions();
    }
    this.scrollToTop();
  }

  getLeftNavDetails(id: number | null): void {
    this.globalEngagementService
      .getLeftNavDetails(id)
      .pipe(takeUntil(this.#unsubscriber$))
      .subscribe({
        next: (response: GlobalEngagementLeftNavModel[]) => {
          this.leftNavDetails = response;
          this.engagementTitle = response[0]?.engagementTitle;
          this.queryParamSubmissionId =
            this.route.snapshot.queryParams['submissionId'];
          if (this.queryParamSubmissionId) {
            if (this.queryParamSubmissionId) {
              this.activeSubmissionId = parseInt(this.queryParamSubmissionId);
            }
            const activeIndex = this.leftNavDetails.findIndex(
              (item: GlobalEngagementLeftNavModel) =>
                item.submissionId.toString() ===
                this.activeSubmissionId?.toString()
            );
            this.setActive(activeIndex);
            // this.scrollToActiveIndex(); //TODO: need to fix
          } else {
            this.updateRoleAndPermissions();
          }
        },
        error: (err) => {
          console.error('An error occurred during submission: ', err);
        },
      });
  }

  updatePermissions(): void {
    // TODO: Instead of this, check if we can reuse the updateRoleAndPermissions
    this.securityWebapiService
      .getPermissionsByEmployeeId(
        EntityDetails.None,
        EntityType.GlobalEngagement
      )
      .then(() => {
        this.permissionCheck.updatePermissions();
      })
      .catch((error: HttpErrorResponse) => {
        console.error('Error fetching permissions:', error);
      });
  }

  scrollToActiveIndex(): void {
    if (this.activeIndex !== -1 && this.leftNavDetails[this.activeIndex]) {
      const elementId = `navItem-${this.leftNavDetails[
        this.activeIndex
      ].submissionId.toString()}`;
      const element = document.getElementById(elementId);
      if (element) {
        const leftMenu = document.getElementById('left-nav-items');
        if (leftMenu) {
          leftMenu.scrollTop = element.offsetTop - leftMenu.offsetTop;
        } else {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    }
  }

  updateRoleAndPermissions(): void {
    const entityId: string | number =
      this.activeSubmissionId && this.activeIndex !== -1
        ? this.activeSubmissionId
        : this.leftNavDetails[0]?.submissionId ?? -1;

    const entityTypeId: number = EntityType.GlobalEngagement;
    this.userService.setEntityDetails(entityId, entityTypeId);
  }

  scrollToTop(): void {
    this.headerService.scrollToTop();
  }

  isEngagementFormValid(isValid: boolean): void {
    this.isEngagementDetailsValid = isValid;
    const formValid:SubmissionFormValidEmitter ={
      formStatus: isValid,
      submissionId: this.activeSubmissionId ?? 0
    }
    this.setFormValidationStatus(formValid);
  }

  setFormValidationStatus(isValid: SubmissionFormValidEmitter): void {
    const targetItemIndex = this.leftNavDetails.findIndex(
      (item: GlobalEngagementLeftNavModel) =>
        item.submissionId === this.activeSubmissionId
    );
    if (targetItemIndex !== -1) {
      this.leftNavDetails[targetItemIndex].isFormCompleted =
        isValid?.formStatus;
    }
  }

   transformStatus(status: string): StatusEnum {
      return (
        Object.values(StatusEnum).find((enumValue) => enumValue === status) ||
        StatusEnum.Draft
      );
    }

  ngOnDestroy(): void {
    this.globalEngagementService.changeSubmissionId({
      opportunitynumber: '',
      submissionid: null,
    });
    this.commonService.enableAuditTrail(false);
    this.#unsubscriber$.next();
    this.#unsubscriber$.complete();
  }
}
